/**
 * One destination root, mounted for the life of the document (F-NAV-03).
 *
 * The unselected roots are hidden three separate ways, and all three are
 * load-bearing:
 *
 * - `opacity: 0` + `pointer-events: none` (in CSS) hide it visually and from
 *   the pointer while **keeping it laid out**. `display: none` would be simpler
 *   and would destroy the very thing this component exists to preserve: an
 *   element with no box has no scroll offset to come back to.
 * - `inert` removes it from the tab order and from hit-testing, which
 *   `aria-hidden` alone does not do — a screen-reader user would otherwise
 *   still tab into an invisible Journal.
 * - `aria-hidden` removes it from the accessibility tree, which `inert` does
 *   not guarantee in every engine yet.
 *
 * What survives, and what F-NAV-03 names: scroll position, navigation depth,
 * and in-progress form drafts. All three are ordinary React and DOM state of a
 * subtree that was never unmounted.
 */

import type { ReactNode } from "react";
import type { DestinationId } from "./destinations";
import styles from "./AppShell.module.css";

export interface DestinationRootProps {
  readonly destination: DestinationId;
  readonly title: string;
  readonly active: boolean;
  readonly children?: ReactNode;
}

export function DestinationRoot({
  destination,
  title,
  active,
  children,
}: DestinationRootProps): ReactNode {
  return (
    <section
      id={`destination-${destination}`}
      data-testid={`destination.${destination}`}
      aria-label={title}
      aria-hidden={active ? undefined : true}
      inert={!active}
      data-ff-active={String(active)}
      className={active ? styles.root : `${styles.root} ${styles.rootHidden}`}
    >
      {children}
    </section>
  );
}
